import { handleError } from "containers/PageLogin/util";
import { useFormik } from "formik";
import { IoMdClose } from "react-icons/io";
import { validationSchemaPassword } from "./schema";
import { FormValuesPwd, ModalPasswordProps, ShowPassword } from "./type";
import {
  FaRegEye as EyeOpen,
  FaRegEyeSlash as EyeClosed,
} from "react-icons/fa";
import { useState } from "react";
import { isPasswordValid } from "./utils";
import { changePassword } from "services/fetch-change-password";
import toast from "react-hot-toast";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useMetadataUser } from "contexts/metadata-user-context";
import { removeCookie } from "utils/functions-cookie";

export function ModalPassword({
  setModalPassword,
}: Readonly<ModalPasswordProps>) {
  const [showPassword, setShowPassword] = useState<ShowPassword>({
    passwordDefault: false,
    password: false,
    confirmPassword: false,
  })
  const [loading, setLoading] = useState(false);
  const { profile } = useMetadataUser();

  const formikPassword = useFormik<FormValuesPwd>({
    initialValues: {
      passwordDefault: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchemaPassword,
    onSubmit: async (values) => {
      setLoading(true);
      const data = {
        current_password: values.passwordDefault,
        new_password: values.password,
      };

      try {
        const res = await changePassword(data, profile?.id ?? "");
        
        if (res.message) {
          setLoading(false);
          toast.success(res.message);
          removeCookie("auth_token");
          removeCookie("refresh_token");
          window.location.href = "/login";
        }
      } catch (error) {
        setLoading(false);
        if ((error as Error).message === "Attempt limit exceeded, please try after some time.") {
          toast.error("Limite de tentativas excedido, tente novamente mais tarde");
        }
        if ((error as Error).message === "Incorrect username or password.") {
          toast.error("Senha atual incorreta");
        }
      } finally {
        formikPassword.resetForm();
        setModalPassword(false);
      }
    },
  });

  return (
    <section className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 shadow-2xl">
      <div className="bg-white w-full m-4 md:m-auto md:w-1/3 relative dark:bg-neutral-800 p-8 rounded-2xl shadow-lg">
        <div className="flex justify-between items-center">
          <h1 className="flex-1 text-xl font-bold text-center">Alterar Senha</h1>
          <button
            className="text-neutral-500 dark:text-neutral-400 absolute top-4 right-4"
            onClick={() => setModalPassword(false)}
          >
            <IoMdClose size={20} />
          </button>
        </div>
        <form
          className="w-full mt-4"
          onSubmit={formikPassword.handleSubmit}
        >
          <section className="w-full md:w-4/5 h-full flex flex-col justify-start items-start">
            <div className="mb-4 w-full relative">
              <label
                htmlFor="passwordDefault"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                Senha Atual
              </label>
              <input
                type={showPassword.passwordDefault ? "text" : "password"}
                name="passwordDefault"
                id="passwordDefault"
                placeholder="********"
                autoComplete="current-password"
                value={formikPassword.values.passwordDefault}
                onChange={formikPassword.handleChange}
                onBlur={formikPassword.handleBlur}
                className={`${handleError("passwordDefault", formikPassword)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formikPassword.touched.passwordDefault && formikPassword.errors.passwordDefault ? (
                <p className="text-red-500 text-xs mt-1">{formikPassword.errors.passwordDefault}</p>
                ) : null}
              {showPassword.passwordDefault ? (
                <EyeOpen
                  className="absolute top-10 right-4 text-neutral-500 dark:text-neutral-400 cursor-pointer"
                  onClick={() => setShowPassword({ ...showPassword, passwordDefault: false })}
                />
              ) : (
                <EyeClosed
                  className="absolute top-10 right-4 text-neutral-500 dark:text-neutral-400 cursor-pointer"
                  onClick={() => setShowPassword({ ...showPassword, passwordDefault: true })}
                />
              )}
            </div>
            <div className="mb-4 w-full relative">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                Nova Senha
              </label>
              <input
                type={showPassword.password ? "text" : "password"}
                name="password"
                id="password"
                placeholder="********"
                autoComplete="new-password"
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onDrag={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                value={formikPassword.values.password}
                onChange={formikPassword.handleChange}
                onBlur={formikPassword.handleBlur}
                className={`${handleError("password", formikPassword)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formikPassword.touched.password && formikPassword.errors.password ? (
                <p className="text-red-500 text-xs mt-1">{formikPassword.errors.password}</p>
                ) : null}
              {showPassword.password ? (
                <EyeOpen
                  className="absolute top-10 right-4 text-neutral-500 dark:text-neutral-400 cursor-pointer"
                  onClick={() => setShowPassword({ ...showPassword, password: false })}
                />
              ) : (
                <EyeClosed
                  className="absolute top-10 right-4 text-neutral-500 dark:text-neutral-400 cursor-pointer"
                  onClick={() => setShowPassword({ ...showPassword, password: true })}
                />
              )}
              <div className="ml-4 text-green-500 text-sm md:text-base mt-4">
                <span
                  className={`${
                    isPasswordValid(formikPassword.values.password)
                      ? ""
                      : "text-red-500"
                  }`}
                >
                  Tamanho mínimo de 8 caracteres
                </span>
                <br />
                <span
                  className={`${
                    /[!@#$%^&*(),.?":{}|<>]/.test(formikPassword.values.password)
                      ? ""
                      : "text-red-500"
                  }`}
                >
                  Deve conter pelo menos 1 caractere especial
                </span>
                <br />
                <span
                  className={`${
                    /[A-Z]/.test(formikPassword.values.password) ? "" : "text-red-500"
                  }`}
                >
                  Deve conter pelo menos 1 letra maiúscula
                </span>
                <br />
                <span
                  className={`${
                    /[a-z]/.test(formikPassword.values.password) ? "" : "text-red-500"
                  }`}
                >
                  Deve conter pelo menos 1 letra minúscula
                </span>
                <br />
                <span
                  className={`${
                    /\d/.test(formikPassword.values.password) ? "" : "text-red-500"
                  }`}
                >
                  Deve conter pelo menos 1 número
                </span>
              </div>
            </div>
            <div className="mb-8 w-full relative">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                Confirmar Senha
              </label>
              <input
                type={showPassword.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                id="confirmPassword"
                autoComplete="new-password"
                placeholder="********"
                value={formikPassword.values.confirmPassword}
                onChange={formikPassword.handleChange}
                onBlur={formikPassword.handleBlur}
                className={`${handleError("confirmPassword", formikPassword)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formikPassword.touched.confirmPassword && formikPassword.errors.confirmPassword ? (
                <p className="text-red-500 text-xs mt-1">{formikPassword.errors.confirmPassword}</p>
              ) : null}
              {showPassword.confirmPassword ? (
                <EyeOpen
                  className="absolute top-10 right-4 text-neutral-500 dark:text-neutral-400 cursor-pointer"
                  onClick={() => setShowPassword({ ...showPassword, confirmPassword: false })}
                />
              ) : (
                <EyeClosed
                  className="absolute top-10 right-4 text-neutral-500 dark:text-neutral-400 cursor-pointer"
                  onClick={() => setShowPassword({ ...showPassword, confirmPassword: true })}
                />
              )}
            </div>
          </section>
          <div className="w-full flex gap-2 justify-between items-center">
            <button
              type="button"
              className={`
                bg-neutral-200 hover:bg-neutral-400/60 dark:bg-neutral-700 
                hover:dark:bg-neutral-700/60 transition-colors duration-500 
                ease-in-out text-neutral-900 dark:text-neutral-200 px-6 w-full
                py-2 rounded-full disabled:cursor-not-allowed disabled:bg-neutral-200
                disabled:hover:bg-neutral-200 dark:disabled:bg-neutral-700 
                dark:disabled:hover:bg-neutral-700 text-sm md:text-base
              `}
              onClick={() => setModalPassword(false)}
              disabled={formikPassword.isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`
                bg-[#4F46E5] hover:bg-[#4F46E5]/80 hover:dark:bg-[#4F46E5]/60 
                transition-colors duration-500 ease-in-out text-white w-full text-sm md:text-base
                md:px-6 md:py-2 px-4 py-2 rounded-full disabled:cursor-not-allowed disabled:bg-[#4F46E5]
                disabled:hover:bg-[#4F46E5] dark:disabled:bg-[#4F46E5] flex justify-center items-center
              `}
              disabled={formikPassword.isSubmitting || !formikPassword.isValid || !formikPassword.values.passwordDefault || loading}
            >
              {loading ? <LoadingSpinner /> : "Alterar Senha"}
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}
